"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TagSelectionState = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const services_1 = require("../services");
const store_1 = require("../store");
const tag_sort_state_1 = require("./tag.sort.state");
class TagSelectionState {
    constructor(pageState) {
        /**
         * Selected collections by tag.
         *
         * Tag fully selected:
         *     tagId => true
         *
         * Tag partially selected:
         *     tagId => collectionUUID => boolean
         */
        this.selectedByTag = mobx_1.observable.map({}, { deep: false });
        this.visibleTagListObjectIDs = [];
        this.expandedCollectionsUUIDs = [];
        this.lastSelectedTagIndex = -1;
        this.lastSelectedCollectionIndex = -1;
        this.pageState = pageState;
        (0, mobx_1.reaction)((_) => this.pageState.isCtrlShiftPressed, (isCtrlShiftPressed) => {
            if (!isCtrlShiftPressed) {
                this.resetLastSelectedTagIndex();
                this.resetLastSelectedCollectionIndex();
            }
        });
        this.reset();
    }
    reset() {
        this.selectedByTag.clear();
    }
    selectTag(tagId, selected = true) {
        const currentTagIndex = this.visibleTagListObjectIDs.indexOf(tagId);
        if (!this.pageState.isCtrlShiftPressed) {
            if (selected) {
                this.selectedByTag.set(tagId, true);
            }
            else {
                this.selectedByTag.delete(tagId);
            }
        }
        else {
            if (selected) {
                this.selectedByTag.set(tagId, true);
            }
            if (this.lastSelectedTagIndex !== -1 && this.lastSelectedTagIndex !== currentTagIndex) {
                this.findFromRange(this.visibleTagListObjectIDs, this.lastSelectedTagIndex, currentTagIndex, (tagIdInRange) => {
                    this.selectedByTag.set(tagIdInRange, true);
                });
            }
        }
        if (selected) {
            this.lastSelectedTagIndex = currentTagIndex;
        }
        const tagSummary = store_1.AppStore.TagSummary.get(tagId);
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.BigMap, 'Select', `Tag:${selected ? 'On' : 'Off'}`, tagSummary.revisions.length);
    }
    selectAll(tags, selected = true) {
        for (const tag of tags) {
            if (selected) {
                this.selectedByTag.set(tag.objectId, true);
            }
            else {
                this.selectedByTag.delete(tag.objectId);
            }
        }
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.BigMap, 'Select', `EveryTag:${selected ? 'On' : 'Off'}`, tags.length);
    }
    setExpandedCollectionsUUIDs(collectionUUIDs) {
        this.expandedCollectionsUUIDs = collectionUUIDs;
    }
    resetLastSelectedCollectionIndex() {
        this.lastSelectedCollectionIndex = -1;
    }
    resetLastSelectedTagIndex() {
        this.lastSelectedTagIndex = -1;
    }
    setVisibleTagListObjectIDs(tagListUUIDs) {
        this.visibleTagListObjectIDs = tagListUUIDs;
    }
    selectCollection(tagId, collectionUUID, selected = true) {
        const selectedCollections = this.getExpandedTagCollections(tagId);
        const currentCollectionIndex = this.expandedCollectionsUUIDs.indexOf(collectionUUID);
        this.selectedByTag.set(tagId, selectedCollections);
        if (!this.pageState.isCtrlShiftPressed) {
            if (selected) {
                selectedCollections.set(collectionUUID, selected);
            }
            else {
                selectedCollections.delete(collectionUUID);
                if (selectedCollections.size === 0) {
                    this.selectedByTag.delete(tagId);
                }
            }
        }
        else {
            if (selected) {
                selectedCollections.set(collectionUUID, selected);
            }
            if (this.lastSelectedCollectionIndex !== -1 && this.lastSelectedCollectionIndex !== currentCollectionIndex) {
                this.findFromRange(this.expandedCollectionsUUIDs, this.lastSelectedCollectionIndex, currentCollectionIndex, (collectionUUIDInRange) => {
                    selectedCollections.set(collectionUUIDInRange, selected);
                });
            }
        }
        if (selected) {
            this.lastSelectedCollectionIndex = currentCollectionIndex;
        }
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.BigMap, 'Select', `Collection:${selected ? 'On' : 'Off'}`);
    }
    isTagSelected(tagId) {
        return this.selectedByTag.has(tagId);
    }
    isCollectionSelected(tagId, collectionUUID) {
        const selectedCollections = this.selectedByTag.get(tagId);
        if (selectedCollections == null) {
            return false;
        }
        return selectedCollections === true || selectedCollections.has(collectionUUID);
    }
    collectionsSelectedSize(tagId) {
        const selectedCollections = this.selectedByTag.get(tagId);
        if (selectedCollections == null) {
            return 0;
        }
        if (selectedCollections === true) {
            const tagSummary = store_1.AppStore.TagSummary.get(tagId);
            return tagSummary.revisions.length;
        }
        return selectedCollections.size;
    }
    get tagSize() {
        return this.selectedByTag.size;
    }
    get collectionSize() {
        let size = 0;
        for (const tagId of Array.from(this.selectedByTag.keys())) {
            const selectedCollections = this.selectedByTag.get(tagId);
            if (selectedCollections === true) {
                const tagSummary = store_1.AppStore.TagSummary.get(tagId);
                size += tagSummary.revisions.length;
            }
            else {
                size += selectedCollections.size;
            }
        }
        return size;
    }
    get collections() {
        const collectionSummaries = [];
        for (const tagId of this.tags) {
            for (const collectionUUID of this.getCollectionUUIDs(tagId)) {
                const collectionSummary = store_1.AppStore.CollectionSummary.getFetchableByUuid(collectionUUID).value;
                if (collectionSummary != null) {
                    collectionSummaries.push(collectionSummary);
                }
            }
        }
        return tag_sort_state_1.TagSortState.sortSummaries(collectionSummaries);
    }
    get ready() {
        let ready = true;
        // Attempt to load all tags and collection summaries
        const tagIds = Array.from(this.selectedByTag.keys());
        for (const tagId of tagIds) {
            ready = store_1.AppStore.Tag.get(tagId) != null && ready;
            for (const collectionUUID of this.getCollectionUUIDs(tagId)) {
                ready = store_1.AppStore.CollectionSummary.getFetchableByUuid(collectionUUID).value != null && ready;
            }
        }
        return ready;
    }
    get tags() {
        const tags = Array.from(this.selectedByTag.keys())
            .map(tagId => store_1.AppStore.Tag.get(tagId));
        return tag_sort_state_1.TagSortState.sortTags(tags)
            .map(tag => tag.objectId);
    }
    getExpandedTagCollections(tagId) {
        const expandedCollections = mobx_1.observable.map();
        const selectedCollections = this.selectedByTag.get(tagId);
        if (selectedCollections == null) {
            return expandedCollections;
        }
        if (selectedCollections === true) {
            const tagSummary = store_1.AppStore.TagSummary.get(tagId);
            for (const revision of tagSummary.revisions) {
                const collectionSummary = store_1.AppStore.CollectionSummary.get(revision);
                if (collectionSummary != null) {
                    expandedCollections.set(collectionSummary.uuid, true);
                }
            }
            return expandedCollections;
        }
        return selectedCollections;
    }
    getCollectionUUIDs(tagId) {
        const selectedCollections = this.selectedByTag.get(tagId);
        if (selectedCollections === null) {
            return [];
        }
        if (selectedCollections === true) {
            return Array.from(this.getExpandedTagCollections(tagId).keys());
        }
        return Array.from(selectedCollections.keys());
    }
    findFromRange(items, startIndex, endIndex, callback) {
        const start = Math.min(startIndex, endIndex);
        const end = Math.max(startIndex, endIndex);
        for (let i = start; i <= end; i++) {
            callback(items[i]);
        }
    }
}
exports.TagSelectionState = TagSelectionState;
tslib_1.__decorate([
    mobx_1.observable
], TagSelectionState.prototype, "visibleTagListObjectIDs", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TagSelectionState.prototype, "expandedCollectionsUUIDs", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TagSelectionState.prototype, "lastSelectedTagIndex", void 0);
tslib_1.__decorate([
    mobx_1.observable
], TagSelectionState.prototype, "lastSelectedCollectionIndex", void 0);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "selectTag", null);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "selectAll", null);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "setExpandedCollectionsUUIDs", null);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "resetLastSelectedCollectionIndex", null);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "resetLastSelectedTagIndex", null);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "setVisibleTagListObjectIDs", null);
tslib_1.__decorate([
    mobx_1.action
], TagSelectionState.prototype, "selectCollection", null);
tslib_1.__decorate([
    mobx_1.computed
], TagSelectionState.prototype, "collectionSize", null);
tslib_1.__decorate([
    mobx_1.computed
], TagSelectionState.prototype, "collections", null);
tslib_1.__decorate([
    mobx_1.computed
], TagSelectionState.prototype, "ready", null);
tslib_1.__decorate([
    mobx_1.computed
], TagSelectionState.prototype, "tags", null);
//# sourceMappingURL=tag.selection.state.js.map